import { Component, type PropsWithChildren } from "react";

function redactDiagnostic(value: string): string {
  return value
    .replace(/https?:\/\/\S+/gi, "[redacted-url]")
    .replace(/\b(?:PAY|STU)-[A-Z0-9-]+\b/gi, "[redacted-id]")
    .slice(0, 1_000);
}

export function reportReactError(
  kind: "caught" | "recoverable" | "uncaught",
  error: unknown,
  componentStack?: string | null,
): void {
  const diagnostic =
    error instanceof Error
      ? { name: error.name, message: redactDiagnostic(error.message) }
      : { name: "UnknownError", message: "A non-Error value was reported" };
  console.error(`[Chams.Pay] React ${kind} error`, {
    ...diagnostic,
    ...(componentStack ? { componentStack: redactDiagnostic(componentStack) } : {}),
  });
}

export class AppErrorBoundary extends Component<PropsWithChildren, { readonly failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { readonly failed: true } {
    return { failed: true };
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    const french = document.documentElement.lang.toLowerCase().startsWith("fr");

    return (
      <main
        data-testid="application-error"
        dir={french ? "ltr" : "rtl"}
        className="grid min-h-dvh place-items-center bg-canvas p-6 font-sans"
      >
        <section className="w-full max-w-md rounded-3xl border border-border bg-white p-6 text-center shadow-card">
          <h1 className="font-heading text-xl font-bold text-foreground">
            {french ? "Impossible d’afficher cette page" : "تعذر عرض هذه الصفحة"}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            {french
              ? "Une erreur inattendue est survenue. Rechargez l’application pour réessayer."
              : "حدث خطأ غير متوقع. أعد تحميل التطبيق ثم حاول مرة أخرى."}
          </p>
          <button
            type="button"
            className="mt-5 min-h-12 rounded-full bg-primary px-6 font-heading text-sm font-bold text-primary-foreground"
            onClick={() => window.location.reload()}
          >
            {french ? "Recharger l’application" : "إعادة تحميل التطبيق"}
          </button>
        </section>
      </main>
    );
  }
}
